import React from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  type SelectChangeEvent
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Loader, Title, ReloadIconButton } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications, toKilograms } from '../../utils';
import {
  HealthSystemAPIControllersFeedResponsesGetFeedConversionResponse,
  HealthSystemDALEnumsWeightType
} from '../../api/HealthSystemApi';
import { ApiService } from '../../hooks';
import useAuthContext from '../../hooks/useAuthContext';

const WEIGHT_UNIT_LABEL = 'kg';

interface FeedStageData {
  title: string;
  icon: string;
  fcr: number;
  metrics: Array<{
    label: string;
    value: number | string;
    unit: string;
  }>;
}

const FeedConversion: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();

  const apiService = new ApiService();
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());
  const [feedData, setFeedData] =
    React.useState<HealthSystemAPIControllersFeedResponsesGetFeedConversionResponse | null>(null);

  const generateYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const loadFeedConversionData = async (year: number) => {
    if (!state.farmId) {
      notifications.error(t('f-com-no-farm-selected'));
      return;
    }

    setIsBusy(true);

    const fromDate = new Date(year, 0, 1).toISOString();
    const toDate = new Date(year, 11, 31, 23, 59, 59).toISOString();

    apiService.api.feedConversion
      .feedConversionGetFeedConversion({
        FarmId: state.farmId,
        From: fromDate,
        To: toDate
      })
      .then((result) => {
        setFeedData(result.data);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadFeedConversionData(selectedYear);
    }
  }, [loaded]);

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const year = Number(event.target.value);
    setSelectedYear(year);
    setLoaded(false);
  };

  // Every weight is shown in kilograms. The unit the API reports the value in is only used to
  // convert it - it is never surfaced, so two farms read the same number.
  const formatKg = (value: number, weightType: HealthSystemDALEnumsWeightType): string =>
    (toKilograms(value, weightType) ?? 0).toFixed(2);

  const getFeedStages = (): FeedStageData[] => {
    if (!feedData) return [];

    const stages: FeedStageData[] = [];

    if (feedData.gestationFeed) {
      const gf = feedData.gestationFeed;
      stages.push({
        title: t('f-feed-gestation'),
        icon: 'G',
        fcr: gf.feedConversionRatio,
        metrics: [
          {
            label: t('f-feed-total-feed'),
            value: formatKg(gf.weight, gf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-number-of-animals'),
            value: gf.numberOfAnimals,
            unit: t('f-sows')
          },
          {
            label: t('f-feed-avg-feed-per-animal'),
            value: formatKg(gf.averageFeedPerAnimal, gf.weightType),
            unit: `${WEIGHT_UNIT_LABEL}/${t('f-sow')}`
          },
          {
            label: t('f-feed-total-litter-birth-weight'),
            value: formatKg(gf.averageLitterBirthWeight, gf.weightType),
            unit: WEIGHT_UNIT_LABEL
          }
        ]
      });
    }

    if (feedData.lactationFeed) {
      const lf = feedData.lactationFeed;
      stages.push({
        title: t('f-feed-lactation'),
        icon: 'L',
        fcr: lf.feedConversionRatio,
        metrics: [
          {
            label: t('f-feed-total-feed'),
            value: formatKg(lf.weight, lf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-number-of-animals'),
            value: lf.numberOfAnimals,
            unit: t('f-sows')
          },
          {
            label: t('f-feed-avg-feed-per-animal'),
            value: formatKg(lf.averageFeedPerAnimal, lf.weightType),
            unit: `${WEIGHT_UNIT_LABEL}/${t('f-sow')}`
          },
          {
            label: t('f-feed-total-litter-birth-weight'),
            value: formatKg(lf.averageLitterBirthWeight, lf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-total-weaned-weight'),
            value: formatKg(lf.averageWeanedWeight, lf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-net-weight-gain'),
            value: formatKg(lf.netWeightGain, lf.weightType),
            unit: WEIGHT_UNIT_LABEL
          }
        ]
      });
    }

    if (feedData.nurseryFeed) {
      const nf = feedData.nurseryFeed;
      stages.push({
        title: t('f-feed-nursery'),
        icon: 'N',
        fcr: nf.feedConversionRatio,
        metrics: [
          {
            label: t('f-feed-total-feed'),
            value: formatKg(nf.weight, nf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-nursery-avg-feed-per-animal'),
            value: formatKg(nf.averageFeedPerAnimal, nf.weightType),
            unit: `${WEIGHT_UNIT_LABEL}/${t('f-piglet')}`
          },
          {
            label: t('f-feed-total-weaned-weight'),
            value: formatKg(nf.averageWeanedWeight, nf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-avg-ten-weeks-weight'),
            value: formatKg(nf.averageTenWeeksWeight, nf.weightType),
            unit: WEIGHT_UNIT_LABEL
          },
          {
            label: t('f-feed-net-weight-gain'),
            value: formatKg(nf.netWeightGain, nf.weightType),
            unit: WEIGHT_UNIT_LABEL
          }
        ]
      });
    }

    return stages;
  };

  const getSummaryMetrics = () => {
    if (!feedData?.summary) return null;

    return {
      totalFeed: formatKg(feedData.summary.totalFeed, feedData.summary.weightType),
      totalAnimals: feedData.summary.totalAnimalsCount,
      averageFCR: feedData.summary.averageFeedConversionRatio.toFixed(2),
      totalWeightGain: formatKg(feedData.summary.totalWeightGain, feedData.summary.weightType),
      unit: WEIGHT_UNIT_LABEL
    };
  };

  const renderStageCard = (stage: FeedStageData) => (
    <Grid item xs={12} md={6} key={stage.title} style={{ display: 'flex' }}>
      <Paper sx={{ p: 3, backgroundColor: '#FFFFFF', borderRadius: '8px' }} elevation={2} style={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Box sx={STAGE_ICON_BOX}>
            <Typography sx={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 700 }}>{stage.icon}</Typography>
          </Box>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600, color: 'text.primary' }}>
            {stage.title}
          </Typography>
          <Chip label={`FCR: ${stage.fcr.toFixed(2)}`} sx={{ fontWeight: 600 }} size="small" />
        </Box>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {stage.metrics.map((metric, idx) => (
            <Grid item xs={6} key={idx}>
              <Box sx={METRIC_BOX}>
                <Typography sx={METRIC_LABEL}>{metric.label}</Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary' }}>
                  {metric.value}{' '}
                  <span className="text-[0.875rem] font-normal text-[rgba(0,0,0,0.54)]">{metric.unit}</span>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Grid>
  );

  const renderSummarySection = () => {
    const summary = getSummaryMetrics();
    if (!summary) return null;

    return (
      <Grid item xs={12}>
        <Paper sx={SUMMARY_CARD} elevation={2}>
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 2 }}>
            {t('f-feed-summary')} - {selectedYear}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography sx={SUMMARY_METRIC_LABEL}>{t('f-feed-total-feed')}</Typography>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#FFFFFF' }}>
                  {summary.totalFeed} {summary.unit}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography sx={SUMMARY_METRIC_LABEL}>{t('f-feed-total-animals')}</Typography>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#FFFFFF' }}>
                  {summary.totalAnimals}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography sx={SUMMARY_METRIC_LABEL}>{t('f-feed-average-fcr')}</Typography>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#FFFFFF' }}>
                  {summary.averageFCR}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography sx={SUMMARY_METRIC_LABEL}>{t('f-feed-total-weight-gain')}</Typography>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#FFFFFF' }}>
                  {summary.totalWeightGain} {summary.unit}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  };

  const feedStages = getFeedStages();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container direction="row" sx={{ justifyContent: 'space-between', mb: 2 }}>
          <Grid item container md={6} xs={10} spacing={1}>
            <Grid item>
              <TrendingUpIcon color="primary" fontSize="large" />
            </Grid>
            <Grid item>
              <Title>{t('f-feed-conversion')}</Title>
            </Grid>
          </Grid>
          <Grid item container sx={{ justifyContent: 'flex-end', alignItems: 'center' }} md={6} xs={10}>
            <Grid item>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={3}>
        <FormControl variant="standard" fullWidth size="small">
          <InputLabel id="year-select-label">{t('f-feed-select-year')}</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={selectedYear}
            onChange={handleYearChange}
            disabled={isBusy}
          >
            {generateYearOptions().map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          <Typography variant="body2">{t('f-feed-info-banner')}</Typography>
        </Alert>
      </Grid>

      {isBusy && (
        <Grid item xs={12}>
          <Loader isLoading={true} size={LoaderSize.DEFAULT_SIZE} />
        </Grid>
      )}

      {loaded && !isBusy && feedData && feedStages.map((stage) => renderStageCard(stage))}

      {loaded && !isBusy && feedData && renderSummarySection()}

      {loaded && !isBusy && !feedData && (
        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary" align="center">
            {t('f-feed-no-data')}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default FeedConversion;

const STAGE_ICON_BOX = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: 'primary.main',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
} as const;

const METRIC_BOX = {
  p: 2,
  backgroundColor: '#F7FAFC',
  borderRadius: '4px',
  minHeight: 80,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
} as const;

const METRIC_LABEL = {
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 1,
  letterSpacing: '0.5px'
} as const;

const SUMMARY_CARD = {
  p: 3,
  pb: 4,
  background: 'linear-gradient(135deg, #5B6FB5 0%, #7B8FD5 100%)',
  borderRadius: '8px'
} as const;

const SUMMARY_METRIC_LABEL = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.9)',
  mb: 1,
  textTransform: 'uppercase'
} as const;
